import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Collection } from '@/lib/taxonomy';
import { CollectionIcon } from '@/components/CollectionInfo';
import { cn } from '@/lib/utils';

interface CollectionBarProps {
  /** Every collection; the main holdings are always shown, others only when selected. */
  collections: ReadonlyArray<Collection>;
  /** The selected collection id, if one is chosen. */
  active: string | null;
  /**
   * True only in the default view: no category, format, filter or search. "All"
   * is lit only then, so it never claims "everything" while something narrows
   * the view.
   */
  isDefault?: boolean;
  /** Exact record count per collection id. Omit while still loading. */
  counts?: Record<string, number>;
  /** Records in the whole archive. */
  total?: number;
  /** A collection id, or null for "All" (the full reset). */
  onSelect: (id: string | null) => void;
  /** The search field. */
  search: ReactNode;
  /** The Filters button. */
  filters: ReactNode;
  /** The record-type switch (Any / Image / Video / Doc / Text). */
  formats: ReactNode;
  /**
   * The id of the block of section cards above the toolbar. While those cards
   * are on screen the toolbar has no section chips (the cards ARE the sections);
   * once they have scrolled away, a row of chips slides in beneath the toolbar
   * so any section is still one tap away.
   */
  cardsId?: string;
}

/** Where the results begin: the marker at the top of the feed in Index.tsx. */
const RESULTS_ID = 'archive-results';
/** Header bar + toolbar + the chip row; results scroll to just below all of it. */
const STICKY_OFFSET = 204;

/** Scroll distance (px) needed to flip direction, so tiny jitters are ignored. */
const DIRECTION_DELTA = 8;
/** The bar always shows for this many px after the Archive begins (on arrival). */
const ARRIVAL_ZONE = 400;

/**
 * True while the reader is scrolling down through the feed, so the bar can tuck
 * away and give the records the room. Scrolling up brings it straight back.
 * It is always shown on arrival at the Archive and near the top of the page.
 */
function useHideOnScrollDown(): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let last = window.scrollY;
    let frame = 0;

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      // Measured from where the records begin (not the top of the panel above
      // them), so the toolbar is always there on arrival at the feed.
      const resultsTop = document.getElementById(RESULTS_ID)?.getBoundingClientRect().top;
      const arriving = resultsTop === undefined || resultsTop > -ARRIVAL_ZONE;

      if (arriving || y < last - DIRECTION_DELTA) {
        setHidden(false);
        last = y;
      } else if (y > last + DIRECTION_DELTA) {
        setHidden(true);
        last = y;
      }
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return hidden;
}

/** True once the element with this id has scrolled up behind the header. */
function useScrolledPast(id: string | undefined): boolean {
  const [past, setPast] = useState(false);

  useEffect(() => {
    if (!id) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const bottom = document.getElementById(id)?.getBoundingClientRect().bottom;
      setPast(bottom !== undefined && bottom < 48);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [id]);

  return past;
}

/**
 * The archive's one toolbar. It sits directly under the section cards and then
 * sticks under the header for the whole feed, so search, filters and the record
 * type are always within reach, and there is exactly one copy of each.
 *
 *   phone:   [ search ........ ] [Filters]
 *            [ Any | Image | Video | Doc | Text ]
 *   desktop: [ search ...... ] [Filters]   [ Any | Image | Video | Doc | Text ]
 *
 * Tucks away as you scroll down and returns the moment you scroll up. Once the
 * section cards are out of view, a row of section chips slides in below it.
 */
export function CollectionBar({
  collections,
  active,
  isDefault = active === null,
  counts,
  total,
  onSelect,
  search,
  filters,
  formats,
  cardsId,
}: CollectionBarProps) {
  const chipsRef = useRef<HTMLElement | null>(null);
  const scrolledAway = useHideOnScrollDown();
  const cardsGone = useScrolledPast(cardsId);
  const showChips = Boolean(cardsId) && cardsGone;

  // Never tuck the bar away while a control inside it has focus (e.g. while the
  // reader is typing in the search field), or the field would vanish under them.
  const [focusWithin, setFocusWithin] = useState(false);
  const hidden = scrolledAway && !focusWithin;

  // Main holdings always; an action collection (e.g. Resources) only while it
  // is the active one, so the current selection is never invisible.
  // Collections that lead off the site (Bitcoin → the explorer) are not filters
  // on this archive, so they don't belong in a strip of filters.
  const items = collections.filter((c) => !c.href && (c.kind === 'main' || c.id === active));

  // Keep the selected chip in view when the chip row is scrolled sideways.
  //
  // This moves the row's own scroller ONLY. It must never use `scrollIntoView`,
  // which also scrolls the page itself vertically to reveal the element: that
  // threw every visitor down to this bar on load.
  useEffect(() => {
    const list = chipsRef.current;
    const chip = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!list || !chip) return;
    const target = chip.offsetLeft - (list.clientWidth - chip.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
  }, [active, showChips]);

  const choose = (id: string | null) => {
    onSelect(id);
    // If the reader is deep in the feed, bring them back to the top of the
    // results so a shorter list doesn't strand them near the footer.
    requestAnimationFrame(() => {
      const results = document.getElementById(RESULTS_ID);
      if (results && results.getBoundingClientRect().top < STICKY_OFFSET) {
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
          top: results.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }
    });
  };

  return (
    <div
      // Slides up beneath the header bar (which sits above it) when hidden.
      // `inert` keeps its controls out of the tab order and away from screen
      // readers while it is tucked away.
      inert={hidden}
      onFocusCapture={() => setFocusWithin(true)}
      onBlurCapture={() => setFocusWithin(false)}
      className={cn(
        'sticky top-12 z-40 border-b border-foreground/15 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85',
        'shadow-[0_8px_16px_-14px_rgba(40,25,10,0.45)]',
        'transition-transform duration-300 ease-out motion-reduce:transition-none',
        hidden && '-translate-y-full',
      )}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-2.5 lg:flex-row lg:items-center lg:gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1 lg:w-56 lg:flex-none xl:w-64">{search}</div>
          {filters}
        </div>
        <div className="min-w-0 lg:flex-1">{formats}</div>
      </div>

      {/* The section chips: only once the cards are behind us. They hang from the
          toolbar's lower edge rather than adding to its height, so they appear
          without pushing the records down. */}
      {showChips && (
        <nav
          ref={chipsRef}
          aria-label="Sections of the archive"
          className="absolute inset-x-0 top-full border-b border-foreground/15 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85"
        >
          <div className="mx-auto flex h-11 max-w-6xl items-center gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Chip label="All" count={total} pressed={isDefault} onClick={() => choose(null)} />
            <span aria-hidden="true" className="mx-1 h-4 w-px shrink-0 bg-foreground/25" />
            {items.map((c) => (
              <Chip
                key={c.id}
                label={c.title}
                icon={<CollectionIcon collection={c} className="h-4 w-4 shrink-0" />}
                count={counts?.[c.id]}
                pressed={active === c.id}
                onClick={() => choose(c.id)}
              />
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}

function Chip({
  label,
  icon,
  count,
  pressed,
  onClick,
}: {
  label: string;
  icon?: ReactNode;
  count?: number;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(
        'inline-flex h-8 shrink-0 items-center gap-2 rounded-full border px-3.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        pressed
          ? 'border-ink bg-ink text-background'
          : 'border-foreground/25 text-foreground hover:border-ink/60 hover:bg-ink/[0.06]',
      )}
    >
      {icon}
      <span className="font-engraved text-[10px] font-semibold uppercase tracking-[0.16em]">{label}</span>
      {typeof count === 'number' && (
        <span
          className={cn(
            'font-mono text-[10px] leading-none tabular-nums',
            pressed ? 'text-background/75' : 'text-foreground/70',
          )}
        >
          {count.toLocaleString()}
        </span>
      )}
    </button>
  );
}
